import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api as http } from '../lib/api.ts';
import { AuthView } from './auth.tsx';
import { PageView } from './page.tsx';
import { Sidebar } from './sidebar.tsx';
import { boot, createPage, flushText, openPage, setState, useStore } from './store.ts';

export function App() {
  const ready = useStore((s) => s.ready);
  const signedIn = useStore((s) => s.signedIn);
  const error = useStore((s) => s.error);
  const finding = useStore((s) => s.finding);

  useEffect(() => { void boot(); }, []);

  // Anything typed in the last half-second still gets written on the way out.
  useEffect(() => {
    const onLeave = () => { void flushText(); };
    window.addEventListener('pagehide', onLeave);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') onLeave();
    });
    return () => window.removeEventListener('pagehide', onLeave);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setState({ finding: true });
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'n' && e.shiftKey) {
        e.preventDefault();
        void createPage({});
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '\\') {
        e.preventDefault();
        setState((s) => ({ sidebarOpen: !s.sidebarOpen }));
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  if (!ready) return <div className="booting" />;
  if (!signedIn) return <AuthView />;

  return (
    <div className="shell">
      <Sidebar />
      <PageView />
      {finding ? <Find onClose={() => setState({ finding: false })} /> : null}
      {error ? (
        <div className="toast" role="status" onClick={() => setState({ error: null })}>{error}</div>
      ) : null}
    </div>
  );
}

type Hits = {
  pages: { id: string; title: string; icon: string; is_database: boolean }[];
  blocks: { id: string; page_id: string; title: string; icon: string; excerpt: string }[];
};

function Find({ onClose }: { onClose: () => void }) {
  const recents = useStore((s) => s.recents);
  const pages = useStore((s) => s.pages);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<Hits | null>(null);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (!q.trim()) { setHits(null); return; }
    const timer = window.setTimeout(() => {
      void http.get<Hits>(`/api/find?q=${encodeURIComponent(q)}`)
        .then(setHits)
        .catch(() => setHits({ pages: [], blocks: [] }));
    }, 160);
    return () => window.clearTimeout(timer);
  }, [q]);

  const rows = q.trim()
    ? [
        ...(hits?.pages ?? []).map((p) => ({ id: p.id, icon: p.icon, title: p.title, sub: '' })),
        ...(hits?.blocks ?? []).map((b) => ({
          id: b.page_id, icon: b.icon, title: b.title, sub: b.excerpt.replace(/«|»/g, ''),
        })),
      ]
    : recents.map((id) => pages[id]).filter(Boolean).map((p) => ({
        id: p!.id, icon: p!.icon, title: p!.title, sub: '',
      }));

  const go = (id: string) => { onClose(); void openPage(id); };

  return createPortal(
    <div className="find" role="dialog" aria-label="Search" onClick={onClose}>
      <div className="find-box" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          className="find-input"
          value={q}
          placeholder="Search pages and their contents…"
          aria-label="Search"
          onChange={(e) => { setQ(e.target.value); setActive(0); }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { onClose(); return; }
            if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, rows.length - 1)); }
            if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
            if (e.key === 'Enter') {
              e.preventDefault();
              const row = rows[active];
              if (row) go(row.id);
            }
          }}
        />
        <div className="find-list">
          {rows.length ? rows.map((row, i) => (
            <button
              key={`${row.id}-${i}`}
              type="button"
              className={`find-row${i === active ? ' on' : ''}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => go(row.id)}
            >
              <span className="find-icon" aria-hidden>{row.icon || '📄'}</span>
              <span className="find-text">
                <span className="find-title">{row.title || 'Untitled'}</span>
                {row.sub ? <span className="find-sub">{row.sub}</span> : null}
              </span>
            </button>
          )) : (
            <div className="find-none">{q.trim() ? 'Nothing matches that.' : 'No pages opened yet.'}</div>
          )}
        </div>
        <div className="find-foot">
          <kbd>↑</kbd><kbd>↓</kbd> to move · <kbd>Enter</kbd> to open · <kbd>Esc</kbd> to close
        </div>
      </div>
    </div>,
    document.body,
  );
}
